import { format, isToday } from 'date-fns'
import { memo, useMemo, useCallback } from 'react'
import { getDotIndicators } from '../../utils/calendarHelpers'
import './MonthDayCell.css'

// Define color mapping function outside component to prevent re-creation
// Color semantics (consistent with WeeklyCalendar):
// - Green: Scheduled Matches
// - Blue: Proposals (pending coordination)
// - Purple: Opponent Availability
// - Gray: User Availability, Completed Matches
const getItemColor = (item) => {
  // Defensive check for null/undefined or non-object items
  if (!item || typeof item !== 'object') return 'gray'

  // Matches
  if (item.type === 'match') {
    // Green for scheduled matches, Gray for completed/past matches
    return item.status === 'scheduled' ? 'green' : 'gray'
  }

  // Blue for any proposal
  if (item.type === 'proposal') return 'blue'

  // Purple for opponent availability
  if (item.type === 'opponentAvailability') return 'purple'

  // Gray for user availability only
  if (item.type === 'availability') return 'gray'

  return 'gray'
}

/**
 * MonthDayCell - Displays an individual day in the calendar grid
 *
 * @param {Object} props
 * @param {Object} props.day - Day object with date, dateString, isCurrentMonth
 * @param {boolean} props.isCurrentMonth - Whether day belongs to current displayed month
 * @param {boolean} props.isSelected - Whether this day is currently selected
 * @param {Array} props.availability - Array of availability items for this day
 * @param {Array} props.opponentAvailability - Array of opponent availability items for this day
 * @param {Array} props.proposals - Array of match proposals for this day
 * @param {Array} props.matches - Array of scheduled matches for this day
 * @param {Function} props.onDayClick - Callback when day is clicked
 *
 * @example
 * // In parent component, determine isSelected by comparing dates:
 * import { isSameDay } from 'date-fns'
 *
 * const isSelected = selectedDate && isSameDay(selectedDate, day.date)
 * <MonthDayCell
 *   day={day}
 *   isSelected={isSelected}
 *   availability={availabilityForDay}
 *   opponentAvailability={opponentAvailabilityForDay}
 *   proposals={proposalsForDay}
 *   matches={matchesForDay}
 *   onDayClick={handleDayClick}
 * />
 */
function MonthDayCell({
  day,
  isCurrentMonth,
  isSelected = false,
  availability = [],
  opponentAvailability = [],
  proposals = [],
  matches = [],
  onDayClick,
}) {
  const handleClick = useCallback(() => {
    if (onDayClick) {
      onDayClick(day.date)
    }
  }, [onDayClick, day.date])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClick()
      }
    },
    [handleClick],
  )

  // Combine all items and generate dot indicators (memoized)
  // Deduplicate items by color to show at most one dot per color type
  const allItems = useMemo(() => {
    const items = [
      ...availability.map((item) => ({ ...item, type: 'availability' })),
      ...opponentAvailability.map((item) => ({
        ...item,
        type: 'opponentAvailability',
      })),
      ...proposals.map((item) => ({ ...item, type: 'proposal' })),
      ...matches.map((item) => ({ ...item, type: 'match' })),
    ]

    // Deduplicate by color: keep only one item per unique color
    const colorMap = new Map()
    items.forEach((item) => {
      const color = getItemColor(item)
      if (!colorMap.has(color)) {
        colorMap.set(color, item)
      }
    })

    return Array.from(colorMap.values())
  }, [availability, opponentAvailability, proposals, matches])

  const indicators = useMemo(
    () =>
      getDotIndicators(allItems, {
        maxDots: 4, // Max 4 colors: green, blue, purple, gray
        getColor: getItemColor,
      }),
    [allItems],
  )

  const ariaLabel = useMemo(() => {
    const dateStr = format(day.date, 'EEEE, MMMM d, yyyy')
    const itemSummary = []

    if (availability.length > 0) {
      itemSummary.push(
        `${availability.length} my availability ${
          availability.length === 1 ? 'slot' : 'slots'
        }`,
      )
    }
    if (opponentAvailability.length > 0) {
      itemSummary.push(
        `${opponentAvailability.length} opponent availability ${
          opponentAvailability.length === 1 ? 'slot' : 'slots'
        }`,
      )
    }
    if (proposals.length > 0) {
      itemSummary.push(
        `${proposals.length} ${
          proposals.length === 1 ? 'proposal' : 'proposals'
        }`,
      )
    }
    if (matches.length > 0) {
      itemSummary.push(
        `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}`,
      )
    }

    return itemSummary.length > 0
      ? `Select ${dateStr}, ${itemSummary.join(', ')}`
      : `Select ${dateStr}`
  }, [
    day.date,
    availability.length,
    opponentAvailability.length,
    proposals.length,
    matches.length,
  ])

  const cellClasses = [
    'fl__month-grid-day',
    !isCurrentMonth && 'fl__month-grid-day--faded',
    isSelected && 'fl__month-grid-day--selected',
  ]
    .filter(Boolean)
    .join(' ')

  const dateClasses = [
    'fl__month-grid-day__header-date',
    isToday(day.date) && 'fl__is-today',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={cellClasses}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={`day-cell-${day.dateString}`}
      onKeyDown={handleKeyDown}
    >
      <div className="fl__month-grid-day__header">
        <div className={dateClasses}>{format(day.date, 'd')}</div>
      </div>

      {indicators.totalCount > 0 && (
        <div className="fl__month-grid-day__indicators">
          <div className="fl__month-grid-day__dots">
            {indicators.dots.map((color, index) => (
              <span
                key={index}
                className={`fl__dot fl__dot--${color}`}
                aria-hidden="true"
              />
            ))}
          </div>
          {indicators.hasOverflow && (
            <div className="fl__month-grid-day__overflow">
              + {indicators.overflowCount} more
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(MonthDayCell)
